import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Clipboard, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * BulkPasteStripeCard — owner pastes rows copied from the Stripe dashboard
 * "Payments" export (tab-separated or comma-separated). Backend parses date,
 * amount and email out of each row and creates a past-fare booking attributed
 * to the matching customer. Duplicates (via SHA of user+date+amount) are
 * silently skipped so re-pasting the same block is safe.
 *
 * Rows without a matching customer come back as `unmatched` so the owner
 * knows exactly which ones to attribute manually via CustomerLookupCard.
 */
export default function BulkPasteStripeCard() {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doImport = async () => {
    if (!raw.trim()) {
      toast.error("Paste some Stripe rows first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const token = localStorage.getItem("linklite_token");
      const res = await axios.post(
        `${API}/admin/past-fares/bulk-paste`,
        { rows: raw },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(res.data);
      if (res.data.created > 0) {
        toast.success(`Imported ${res.data.created} past fares`);
      } else if (res.data.duplicates > 0) {
        toast.info(`All ${res.data.duplicates} rows were duplicates`);
      } else {
        toast.warning("Nothing was imported — check the unmatched list");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Import failed";
      toast.error(typeof msg === "string" ? msg : "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-[#121212] border-white/5" data-testid="bulk-paste-stripe-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium text-white flex items-center gap-2">
          <Clipboard className="w-4 h-4 text-emerald-400" />
          Bulk-import past fares from Stripe
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-zinc-500 leading-relaxed">
          Copy rows from your Stripe dashboard → Payments → Export (or select cells and copy),
          then paste below. We&apos;ll match each row to a customer by email and create a completed
          past-fare booking (PA 6% tax inclusive). Duplicates are auto-skipped, so re-pasting is safe.
        </p>

        <Textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="Paste rows here. Tab-separated or comma-separated both work.&#10;Example: 2026-05-22 $42.30 alex@example.com"
          rows={8}
          className="bg-black border-white/10 text-xs font-mono"
          data-testid="bulk-paste-textarea"
        />

        <div className="flex items-center gap-2">
          <Button
            onClick={doImport}
            disabled={busy || !raw.trim()}
            className="bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 border border-emerald-500/40 h-9"
            data-testid="bulk-paste-import-btn"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
            ) : (
              <><Clipboard className="w-4 h-4 mr-2" /> Import rows</>
            )}
          </Button>
          {raw && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setRaw(""); setResult(null); }}
              className="h-9 text-xs text-zinc-400 hover:text-white"
              data-testid="bulk-paste-clear-btn"
            >
              Clear
            </Button>
          )}
        </div>

        {result && (
          <div className="space-y-2 pt-2" data-testid="bulk-paste-result">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2">
                <p className="text-lg font-bold text-emerald-300">{result.created}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Created</p>
              </div>
              <div className="rounded-lg bg-zinc-800 border border-white/10 p-2">
                <p className="text-lg font-bold text-zinc-300">{result.duplicates}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Duplicates</p>
              </div>
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2">
                <p className="text-lg font-bold text-amber-300">{result.unmatched?.length ?? 0}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Unmatched</p>
              </div>
            </div>

            {result.unmatched && result.unmatched.length > 0 && (
              <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-2 space-y-1 max-h-48 overflow-y-auto">
                <div className="flex items-center gap-1 text-[11px] text-amber-300 font-semibold mb-1">
                  <AlertTriangle className="w-3 h-3" /> Rows that need manual attribution:
                </div>
                {result.unmatched.slice(0, 20).map((row, i) => (
                  <div key={i} className="text-[11px] text-zinc-400 border-l-2 border-amber-500/40 pl-2 py-0.5" data-testid={`bulk-paste-unmatched-${i}`}>
                    <span className="text-white">{row.email || "no email"}</span> · ${row.amount ?? "?"} · {row.date || "no date"}
                    <span className="text-zinc-500 ml-2">— {row.reason}</span>
                  </div>
                ))}
                {result.unmatched.length > 20 && (
                  <p className="text-[10px] text-zinc-500 italic">…and {result.unmatched.length - 20} more</p>
                )}
              </div>
            )}

            {result.created > 0 && result.unmatched?.length === 0 && (
              <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-2 flex items-center gap-2 text-[11px] text-emerald-300">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Every row matched a customer. Loyalty points + spend totals updated.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
